// File: js/shared/extensions/quizAnswers-create.js
//
// Initialization-only module for quiz-answer sections (`quiz`, `module_quiz`,
// `contest_quiz`). Click/reorder/delete handling lives in `blockEventBus.js`.
// This file:
//   - registers the answer-block factory used by the bus in upload mode
//   - injects quiz-answers-specific afterAdd/onAfterDelete hooks into the bus
//     (richtext sync for new blocks, initMultipleChoice on add+delete)
//   - hydrates richtext sync on existing blocks at page-load
//   - keeps the per-section idempotency guard introduced when this file was
//     the only one in the codebase that had bothered

import { register } from '../events/blockFactoryRegistry.js';
import { overrideKindConfig } from '../events/blockEventBus.js';
import { i18n } from '../utils/config.js';

import {
  setupRichtextSync,
  setupRichtextSyncAll,
  setupGlobalRichtextPasting
} from '../utils/richtextUtils.js';

import { initMultipleChoice } from '../quiz/multiple-choice-toggle.js';

register('quiz-answers', (section, blockName, _type, index) => {
  const showCorrectToggle = section.dataset.showCorrectToggle !== '0';
  return createAnswerBlockUnchecked(blockName, index, null, showCorrectToggle);
});

// Inject quiz-answers-specific hooks. The bus is otherwise unaware of the
// multiple-choice toggle.
overrideKindConfig('quiz-answers', {
  afterAdd: (newBlockEl) => {
    setupRichtextSync(newBlockEl);
    initMultipleChoice(document);
  },
  onAfterDelete: () => {
    initMultipleChoice(document);
  },
});

export function setupQuizAnswers(root = document) {
  setupGlobalRichtextPasting();

  root.querySelectorAll('section[data-block-type="quiz-answers"]').forEach(section => {
    if (!(section instanceof HTMLElement)) return;
    if (section.dataset.quizAnswersBound === '1') return;
    section.dataset.quizAnswersBound = '1';

    setupRichtextSyncAll(section);
  });

  initMultipleChoice(document);
}

// Checkbox ids are DOM-unique tokens, never the index (audit F17,
// 2026-09-13): add / add / delete / add produced two `…-answer-5-correct`
// and the label toggled another answer.
let answerToggleSeq = 0;
export function nextAnswerToggleId(blockName) {
  answerToggleSeq += 1;
  return `${blockName}-answer-correct-${Date.now().toString(36)}-${answerToggleSeq}`;
}

function createAnswerBlockUnchecked(blockName, index, blockId = null, showCorrectToggle = true) {
  const prefix = `${blockName}[${index}]`;
  const idAttr = blockId ? ` data-block-id="${blockId}"` : '';
  const toggleId = nextAnswerToggleId(blockName);

  const correctToggleHtml = showCorrectToggle
    ? `
        <span class="answer-correct-toggle">
          <label class="checkbox-inline" for="${toggleId}">
            <input type="checkbox"
                   id="${toggleId}"
                   name="${prefix}[is_correct]"
                   value="1">
            <span class="toggle-label">${i18n('block.answer.correct_toggle_label')}</span>
          </label>
        </span>
      `
    : '';

  return `
    <div class="block answer-block block-false-answer required-false"${idAttr} data-type="text">
      <div class="block-header">
        <span class="block-label">${i18n('block.upload_label.answer')}</span>

        ${correctToggleHtml}

        <div class="block-actions">
          <button type="button" data-action="up" title="${i18n('block.action.move_up')}">↑</button>
          <button type="button" data-action="down" title="${i18n('block.action.move_down')}">↓</button>
          <button type="button" data-action="delete" title="${i18n('block.action.delete_answer')}">✕</button>
        </div>
      </div>

      <div class="richtext" contenteditable="true" data-name="${prefix}[content]"></div>
      <input type="hidden" name="${prefix}[content]" value="">
      <input type="hidden" name="${prefix}[type]" value="text">
    </div>`;
}
